import React, { useState, useRef } from 'react';
import {
    Box,
    Paper,
    Typography,
    Stack,
    Button,
    alpha
} from '@mui/material';
import { Print as PrintIcon, FileDownload as DownloadIcon } from '@mui/icons-material';
import { formatCurrency } from '../../../utils/compteClientEngine';
import { COLORS, jsonToXlsx } from '../../../settings/themeSettings';

const BalanceGeneraleViewer = ({ data, onSelectClient }) => {
    const [activeRowIdx, setActiveRowIdx] = useState(0);
    const tableRef = useRef(null);

    if (!data) return null;

    const {
        dateSituationStr = 'Ce jour',
        rows = [],
        totalGeneralDebit = 0,
        totalGeneralCredit = 0,
        totalGeneralSolde = 0
    } = data;

    const dateTirage = new Date().toLocaleDateString('fr-FR') + ' à ' + new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

    const handleKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveRowIdx(prev => Math.min(rows.length - 1, prev + 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveRowIdx(prev => Math.max(0, prev - 1));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            const selected = rows[activeRowIdx];
            if (selected && onSelectClient) {
                onSelectClient(selected);
            }
        }
    };

    const handleExportExcel = () => {
        const exportData = rows.map(r => ({
            'Client': r.nom,
            'Débit (Total facturé CFA)': r.debit,
            'Crédit (Total encaissé CFA)': r.credit,
            'Solde dû (CFA)': r.soldeDu
        }));
        jsonToXlsx(exportData, `Balance_Generale_Clients_${dateSituationStr.replace(/\//g, '-')}`);
    };

    const handlePrint = () => {
        window.print();
    };

    return (
        <Box tabIndex={0} ref={tableRef} onKeyDown={handleKeyDown} sx={{ outline: 'none' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                    {rows.length} client{rows.length > 1 ? 's' : ''} avec mouvements à la date du {dateSituationStr}
                </Typography>
                <Stack direction="row" spacing={1}>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PrintIcon />}
                        onClick={handlePrint}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        Imprimer
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={<DownloadIcon />}
                        onClick={handleExportExcel}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        Export Excel
                    </Button>
                </Stack>
            </Stack>

            <Paper
                elevation={0}
                className="printable-document"
                sx={{
                    p: 3,
                    border: '2px solid #0f172a',
                    borderRadius: '8px',
                    bgcolor: 'white',
                    maxWidth: '1000px',
                    mx: 'auto'
                }}
            >
                {/* EN-TÊTE TRANSVERSAL COMMUN */}
                <Box sx={{ borderBottom: '2px solid #0f172a', pb: 1.5, mb: 2 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1.2fr 2fr 1.2fr', alignItems: 'center' }}>
                        <Box>
                            <Typography variant="body1" fontWeight="bold" sx={{ color: '#0f172a' }}>
                                AFRICA FOR TOURISM
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                                Agence de Voyages & Tourisme
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                                TravelManager v2.0
                            </Typography>
                        </Box>

                        <Box textAlign="center">
                            <Typography variant="h5" fontWeight="bold" sx={{ textTransform: 'uppercase', letterSpacing: 1, color: '#0f172a' }}>
                                Balance générale clients
                            </Typography>
                        </Box>

                        <Box textAlign="right">
                            <Typography variant="caption" display="block">
                                <strong>À la date du :</strong> {dateSituationStr}
                            </Typography>
                            <Typography variant="caption" display="block" color="text.secondary">
                                Date de tirage : {dateTirage}
                            </Typography>
                            <Typography variant="caption" display="block" color="text.secondary">
                                Page : 1 / 1
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* TABLEAU MULTI-CLIENTS */}
                <Box sx={{ border: '1px solid #0f172a', borderRadius: '4px', overflow: 'hidden' }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 180px 180px 200px', bgcolor: '#0f172a', color: 'white', p: 1, fontWeight: 'bold', fontSize: '0.85rem' }}>
                        <Box>Client</Box>
                        <Box sx={{ textAlign: 'right' }}>Débit (Total facturé)</Box>
                        <Box sx={{ textAlign: 'right' }}>Crédit (Total encaissé)</Box>
                        <Box sx={{ textAlign: 'right' }}>Solde dû</Box>
                    </Box>

                    <Box sx={{ maxHeight: '55vh', overflowY: 'auto' }}>
                        {rows.length === 0 ? (
                            <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                                Aucun client avec solde ou mouvement à cette date.
                            </Box>
                        ) : (
                            rows.map((r, idx) => {
                                const isSelected = activeRowIdx === idx;
                                return (
                                    <Box
                                        key={r.id || idx}
                                        onClick={() => {
                                            setActiveRowIdx(idx);
                                            if (onSelectClient) onSelectClient(r);
                                        }}
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: '2fr 180px 180px 200px',
                                            p: 1.2,
                                            alignItems: 'center',
                                            fontSize: '0.85rem',
                                            borderBottom: '1px solid #e2e8f0',
                                            bgcolor: isSelected ? alpha(COLORS.primary, 0.12) : (idx % 2 === 0 ? 'white' : '#f8fafc'),
                                            borderLeft: isSelected ? `4px solid ${COLORS.primary}` : '4px solid transparent',
                                            cursor: 'pointer',
                                            '&:hover': {
                                                bgcolor: alpha(COLORS.primary, 0.08)
                                            }
                                        }}
                                    >
                                        <Box sx={{ fontWeight: 700, color: '#0f172a' }}>{r.nom}</Box>
                                        <Box sx={{ textAlign: 'right', fontWeight: 600 }}>
                                            {formatCurrency(r.debit)}
                                        </Box>
                                        <Box sx={{ textAlign: 'right', fontWeight: 600, color: '#16a34a' }}>
                                            {formatCurrency(r.credit)}
                                        </Box>
                                        <Box sx={{ textAlign: 'right', fontWeight: 800, color: r.soldeDu > 0 ? '#dc2626' : (r.soldeDu < 0 ? '#2563eb' : '#0f172a') }}>
                                            {formatCurrency(r.soldeDu)}
                                        </Box>
                                    </Box>
                                );
                            })
                        )}
                    </Box>

                    {/* TOTAL GÉNÉRAL (SECTION 5.5) */}
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: '2fr 180px 180px 200px',
                            bgcolor: '#f1f5f9',
                            p: 1.2,
                            fontWeight: 'bold',
                            fontSize: '0.9rem',
                            borderTop: '2px solid #0f172a'
                        }}
                    >
                        <Box sx={{ fontWeight: 800, color: '#0f172a' }}>
                            TOTAL GÉNÉRAL :
                        </Box>
                        <Box sx={{ textAlign: 'right', color: '#0f172a' }}>
                            {formatCurrency(totalGeneralDebit)}
                        </Box>
                        <Box sx={{ textAlign: 'right', color: '#16a34a' }}>
                            {formatCurrency(totalGeneralCredit)}
                        </Box>
                        <Box sx={{ textAlign: 'right', color: totalGeneralSolde > 0 ? '#dc2626' : '#0f172a', fontWeight: 900 }}>
                            {formatCurrency(totalGeneralSolde)} CFA
                        </Box>
                    </Box>
                </Box>
            </Paper>
        </Box>
    );
};

export default BalanceGeneraleViewer;
